import { JournalLineDraftCreateInput } from './../../../../types/src/mado/input-type/journal-line.draft-create-input';
import { JournalLineUpdateInput } from './../../../../types/src/mado/input-type/journal-line.update-input';
import { JournalLineCreateInput } from './../../../../types/src/mado/input-type/journal-line.create-input';
import { JournalEntryDraftCreateInput } from './../../../../types/src/mado/input-type/journal-entry.draft-create-input';
import { JournalRecordDraftCreateInput } from './../../../../types/src/mado/input-type/journal-record.draft-create-input';
import { JournalEntryUpdateInput } from './../../../../types/src/mado/input-type/journal-entry.update-input';
import { JournalEntryCreateInput } from './../../../../types/src/mado/input-type/journal-entry.create-input';
import { JournalEntryGridObject, JournalEntryObject } from './../../../../types/src/mado/object-type/journal-entry.object';
import { JournalRecordCreateInput } from './../../../../types/src/mado/input-type/journal-record.create-input';
import { JournalRecordUpdateInput, JournalRecordUpdateStateInput } from './../../../../types/src/mado/input-type/journal-record.update-input';
import { Injectable } from '@angular/core';

import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { GridInput } from '@it2go/data-grid';
import {
    JournalRecordAggregationObject,
    JournalRecordGridObject, JournalRecordObject,
} from '@libs/types/src/mado/object-type/journal-record.object';
import { filterFrag } from './types/filter.types';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { JournalLineGridObject, JournalLineObject } from '@libs/types/src/mado/object-type/journal-line.object';

@Injectable({ providedIn: 'root' })
export class JournalGqlService extends GqlServiceV2 {

    public finilizeJournalRecordDraft(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    journalRecord {
                        finalizeDraft(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public finilizeJournalRecordSaved(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    journalRecord {
                        finalizeSaved(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public backToState(input: JournalRecordUpdateStateInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: JournalRecordUpdateStateInput!) {
                    journalRecord {
                        backTo(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getJournalRecords(input: GridInput | null, frag: string) {
        return this.query<JournalRecordGridObject>(
            `
                query($input: GridInput!) {
                    journalRecord {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getJournalRecordAmountAggregations(input: GridInput | null, frag: string) {
        return this.query<JournalRecordAggregationObject>(
            `
                query($input: GridInput!) {
                    journalRecord {
                        amountAggregation(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createJournalRecord(input: JournalRecordCreateInput, frag: string) {
        return this.mutate<JournalRecordObject>(
            `
                mutation($input: JournalRecordCreateInput!) {
                    journalRecord {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createJournalRecordDraft(input: JournalRecordDraftCreateInput, frag: string) {
        return this.mutate<JournalRecordObject>(
            `
                mutation($input: JournalRecordDraftCreateInput!) {
                    journalRecord {
                        createDraft(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateJournalRecord(input: JournalRecordUpdateInput, frag: string) {
        return this.mutate<JournalRecordObject>(
            `
                mutation($input: JournalRecordUpdateInput!) {
                    journalRecord {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteJournalRecord(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    journalRecord {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getJournalEntries(input: GridInput | null, frag: string) {
        return this.query<JournalEntryGridObject>(
            `
                query($input: GridInput!) {
                    journalEntry {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createJournalEntry(input: JournalEntryCreateInput, frag: string) {
        return this.mutate<JournalEntryObject>(
            `
                mutation($input: JournalEntryCreateInput!) {
                    journalEntry {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createJournalEntryDraft(input: JournalEntryDraftCreateInput, frag: string) {
        return this.mutate<JournalEntryObject>(
            `
                mutation($input: JournalEntryDraftCreateInput!) {
                    journalEntry {
                        createDraft(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateJournalEntry(input: JournalEntryUpdateInput, frag: string) {
        return this.mutate<JournalEntryObject>(
            `
                mutation($input: JournalEntryUpdateInput!) {
                    journalEntry {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteJournalEntry(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    journalEntry {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getJournalLines(input: GridInput | null, frag: string) {
        return this.query<JournalLineGridObject>(
            `
                query($input: GridInput!) {
                    journalLine {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createJournalLine(input: JournalLineCreateInput, frag: string) {
        return this.mutate<JournalLineObject>(
            `
                mutation($input: JournalLineCreateInput!) {
                    journalLine {
                        create(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createJournalLineDraft(input: JournalLineDraftCreateInput, frag: string) {
        return this.mutate<JournalLineObject>(
            `
                mutation($input: JournalLineDraftCreateInput!) {
                    journalLine {
                        createDraft(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateJournalLine(input: JournalLineUpdateInput, frag: string) {
        return this.mutate<JournalLineObject>(
            `
                mutation($input: JournalLineUpdateInput!) {
                    journalLine {
                        update(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteJournalLine(input: IdInput, frag = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    journalLine {
                        delete(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
